import { ReactNode } from 'react';
import { Form } from 'react-bootstrap';

/** Visual + screen-reader marker for required fields. Used by FormLabel and inline labels (e.g. checkboxes). */
export const RequiredIndicator = () => (
  <>
    <span className="form-required-indicator" aria-hidden="true">
      *
    </span>
    <span className="visually-hidden"> (required)</span>
  </>
);

export interface FormLabelProps {
  htmlFor?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

/** Drop-in replacement for Form.Label with optional required-field asterisk. */
export const FormLabel = ({ htmlFor, required, children, className }: FormLabelProps) => (
  <Form.Label htmlFor={htmlFor} className={className}>
    {children}
    {required && <RequiredIndicator />}
  </Form.Label>
);

/** One-line legend explaining the required-field asterisk convention. Place once per form or wizard. */
export interface FormRequiredLegendProps {
  inline?: boolean;
  className?: string;
}

export const FormRequiredLegend = ({ inline, className }: FormRequiredLegendProps = {}) => {
  const classes = ['form-required-legend', 'text-muted', 'fs-small', inline ? 'mb-0' : 'mb-2', className]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      <span className="form-required-indicator" aria-hidden="true">
        *
      </span>{' '}
      Required field
    </>
  );

  if (inline) {
    return <span className={classes}>{content}</span>;
  }

  return <p className={classes}>{content}</p>;
};

export interface FormStepHeaderProps {
  title: string;
  showRequiredLegend?: boolean;
  className?: string;
}

/** Step title with optional required-field legend aligned to the right. */
export const FormStepHeader = ({
  title,
  showRequiredLegend = true,
  className = 'mb-3'
}: FormStepHeaderProps) => (
  <div className={`d-flex align-items-center justify-content-between gap-3 ${className}`}>
    <h6
      className="fw-bold mb-0"
      style={{ fontSize: '1.125rem', color: '#5E6469' }}
    >
      {title}
    </h6>
    {showRequiredLegend && <FormRequiredLegend inline className="flex-shrink-0 text-end" />}
  </div>
);
